import React, { useMemo, useState } from 'react';
import { joinLocationLabels } from '../../auth/managedLocations';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Card, Stack } from '@mui/material';
import { Person } from '@mui/icons-material';
import { LocationFilter, SortKey, StaffPerformanceMeta, StaffPerformanceRecord, StaffSuccessUpdates, SuccessUpdatesMeta } from '../types';
import { aggregateTeam, filterRecords, formatInt, groupByLocation, isInactive, shortLocationName, sortRecords } from '../utils/metrics';
import { attachSuccessUpdates, successUpdatesReady, sumSuccessUpdates } from '../utils/successUpdates';
import { categoryFilterLabel } from '../utils/serviceCategories';
import { buildTeamTiles } from '../utils/tiles';
import { ClientUpdatesDrawer } from './ClientUpdatesDrawer';
import { EmptyState } from './EmptyState';
import { KpiTiles } from './KpiTile';
import { LocationCards } from './LocationCards';
import { StaffPerformanceHeader } from './StaffPerformanceHeader';
import { CategoryFilterProps } from './ServiceCategorySelect';
import { LocationOption, TeamFilters } from './TeamFilters';
import { TeamTable } from './TeamTable';

export interface TeamOverviewViewProps {
  records: StaffPerformanceRecord[];
  meta: StaffPerformanceMeta | null;
  role: 'MANAGER' | 'ADMIN';
  /** A manager's locations; with more than one the view gets the admin's location filter and cards. */
  assignedLocations?: string[];
  selfStaffId?: string | null;
  onOpenStaff: (record: StaffPerformanceRecord) => void;
  /**
   * The overdue client-update rollups. Per-coach figures only appear once `meta` is present and
   * `docsLoaded` is true; until then the overview is exactly the sessions / clients / conversion view.
   */
  successUpdates?: {
    byStaffId: Map<string, StaffSuccessUpdates>;
    unassigned: StaffSuccessUpdates | null;
    meta: SuccessUpdatesMeta | null;
    docsLoaded: boolean;
  };
  disableVirtualization?: boolean;
  /** Service-category filter; `records` are already restricted to the selection. */
  categoryFilter?: CategoryFilterProps;
}

/**
 * Manager / admin landing: team tiles, filters, location cards (admins and
 * multi-site managers) and the sortable table. Inactive staff (no sessions in the latest period) are hidden
 * until asked for, then listed in their own table.
 */
export const TeamOverviewView: React.FC<TeamOverviewViewProps> = ({
  records: sourceRecords,
  meta,
  role,
  assignedLocations = [],
  selfStaffId,
  onOpenStaff,
  successUpdates,
  disableVirtualization,
  categoryFilter,
}) => {
  const isAdmin = role === 'ADMIN';
  const multiSite = isAdmin || assignedLocations.length > 1;
  // Links out of this page keep the URL's query string so the category selection follows the user.
  const { search: locationSearch } = useLocation();
  const filterSuffix = categoryFilter?.selected ? ` · ${categoryFilterLabel(categoryFilter.selected)}` : '';
  const updatesMeta = successUpdates?.meta ?? null;
  const byStaffId = successUpdates?.byStaffId;
  const unassigned = successUpdates?.unassigned ?? null;
  // Loading, no successful run yet and a failed docs listener all read as "no data": the page then looks as it did before.
  const hasUpdates = successUpdatesReady(updatesMeta, Boolean(successUpdates?.docsLoaded));
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('sessions');
  const [showInactive, setShowInactive] = useState(false);
  const [location, setLocation] = useState<LocationFilter>('ALL');
  const [openUpdatesFor, setOpenUpdatesFor] = useState<StaffPerformanceRecord | null>(null);

  const records = useMemo(
    () => (hasUpdates && byStaffId ? attachSuccessUpdates(sourceRecords, byStaffId) : sourceRecords),
    [sourceRecords, hasUpdates, byStaffId]
  );
  const groups = useMemo(() => groupByLocation(records), [records]);
  const inScope = useMemo(() => filterRecords(records, { search: '', showInactive: true, location }), [records, location]);
  const aggregates = useMemo(() => aggregateTeam(inScope), [inScope]);
  const periods = records[0]?.periods ?? meta?.periods ?? null;
  const tiles = useMemo(
    () => buildTeamTiles(aggregates, periods, hasUpdates ? sumSuccessUpdates(inScope) : undefined),
    [aggregates, periods, hasUpdates, inScope]
  );

  const activeRows = useMemo(
    () => sortRecords(filterRecords(records, { search, showInactive: false, location }), sortKey),
    [records, search, location, sortKey]
  );
  const inactiveRows = useMemo(
    () => sortRecords(filterRecords(records, { search, showInactive: true, location }).filter(isInactive), sortKey),
    [records, search, location, sortKey]
  );
  const inactiveCount = inScope.filter(isInactive).length;

  const locationOptions: LocationOption[] | undefined = multiSite
    ? [
        { value: 'ALL', label: 'All locations', count: records.length },
        ...groups.map((group) => ({
          value: (group.location === null ? 'NONE' : group.location) as LocationFilter,
          label: group.label,
          count: group.records.length,
        })),
      ]
    : undefined;

  const subtitle = isAdmin
    ? 'Every location · last 6 months vs the previous 6'
    : `Your team at ${assignedLocations.length > 0 ? joinLocationLabels(assignedLocations, shortLocationName) : shortLocationName(null)} · last 6 months vs the previous 6`;

  return (
    <Box>
      <StaffPerformanceHeader
        subtitle={subtitle}
        periods={periods}
        meta={meta}
        updatesCheckedAt={hasUpdates ? updatesMeta.last_success_at : undefined}
        updatesFailed={hasUpdates && updatesMeta.status === 'error'}
        action={
          selfStaffId ? (
            <Button
              component={RouterLink}
              to={`/staff-performance/${selfStaffId}${locationSearch}`}
              variant="outlined"
              size="small"
              startIcon={<Person />}
            >
              My performance
            </Button>
          ) : undefined
        }
      />

      {records.length === 0 ? (
        <EmptyState
          title="No performance data yet"
          body="The staff performance mirror has not been loaded. An admin can run the first sync from Admin Panel → Data Sync."
        />
      ) : (
        <Stack spacing={3}>
          <KpiTiles tiles={tiles} />

          <TeamFilters
            search={search}
            onSearch={setSearch}
            sortKey={sortKey}
            onSort={setSortKey}
            showInactive={showInactive}
            onToggleInactive={setShowInactive}
            inactiveCount={inactiveCount}
            searchPlaceholder={isAdmin ? 'Find a staff member' : 'Find a team member'}
            locationOptions={locationOptions}
            location={location}
            onLocation={multiSite ? setLocation : undefined}
            categoryFilter={categoryFilter}
          />

          {multiSite ? <LocationCards groups={groups} selected={location} onSelect={setLocation} showUpdates={hasUpdates} /> : null}

          {isAdmin && hasUpdates && unassigned && unassigned.counts.active > 0 ? (
            <Alert severity="info" role="status">
              {unassigned.counts.active === 1
                ? '1 active client has'
                : `${formatInt(unassigned.counts.active)} active clients have`}{' '}
              no lead coach on the staff list · {formatInt(unassigned.counts.overdue)} overdue
            </Alert>
          ) : null}

          <Card elevation={2}>
            <TeamTable
              records={activeRows}
              sortKey={sortKey}
              onSortKey={setSortKey}
              onRowClick={onOpenStaff}
              showLocation={multiSite}
              selfStaffId={selfStaffId}
              caption={`Team · ${inScope.length} staff${filterSuffix}`}
              showUpdates={hasUpdates}
              onOpenUpdates={setOpenUpdatesFor}
              disableVirtualization={disableVirtualization}
            />
          </Card>

          {showInactive && inactiveRows.length > 0 ? (
            <Card elevation={2} sx={{ opacity: 0.85 }}>
              <TeamTable
                records={inactiveRows}
                sortKey={sortKey}
                onSortKey={setSortKey}
                onRowClick={onOpenStaff}
                showLocation={multiSite}
                selfStaffId={selfStaffId}
                caption={`Inactive in the latest 6 months · ${inactiveRows.length} staff${filterSuffix}`}
                showUpdates={hasUpdates}
                onOpenUpdates={setOpenUpdatesFor}
                disableVirtualization={disableVirtualization}
              />
            </Card>
          ) : null}
        </Stack>
      )}

      <ClientUpdatesDrawer record={hasUpdates ? openUpdatesFor : null} onClose={() => setOpenUpdatesFor(null)} />
    </Box>
  );
};
